import { validateSlashPrice as validateSlashPriceRule } from "../../../Routes/Marketplace/libs/productPricing";
import {
  getSizeFieldsForProfile,
  sumSizeStock,
} from "../../../Routes/Marketplace/libs/productSizes";
import { getCategoryFormProfile, normalizeProductAttributes } from "./categoryFormConfig";

export const MAX_PRODUCT_IMAGES = 5;

export const PRODUCT_FIELD_LIMITS = {
  name: 120,
  description: 2000,
  brand: 80,
  color: 40,
  attribute: 120,
  sizeStock: 99999,
};

export const formatNgnPrice = (amount) =>
  (amount || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const parsePriceInput = (raw) => {
  const digits = String(raw).replace(/[^\d]/g, "");
  return digits ? Number(digits) / 100 : 0;
};

export const defaultProductForm = {
  name: "",
  category: "",
  description: "",
  brand: "",
  color: "",
  quantity: "",
  attributes: {},
  sizeSmall: "",
  sizeMedium: "",
  sizeLarge: "",
  sizeSmallextraLarge: "",
  sizeXXLarge: "",
  sizeXXXLarge: "",
};

const getProfileSizeFields = (category) => {
  const profile = getCategoryFormProfile(category);
  if (!profile.showSizes) return [];
  return getSizeFieldsForProfile(profile.sizeMode || "clothing");
};

export const validateProductForm = ({ form, price, compareAtPrice, images }) => {
  const errors = [];
  const profile = getCategoryFormProfile(form.category);
  const sizeFields = getProfileSizeFields(form.category);

  if (!form.name?.trim()) errors.push("Product name is required.");
  if (form.name?.trim().length > PRODUCT_FIELD_LIMITS.name) {
    errors.push(`Product name must be ${PRODUCT_FIELD_LIMITS.name} characters or fewer.`);
  }
  if (!form.category?.trim() || form.category === "Select") errors.push("Category is required.");
  if (!form.description?.trim()) errors.push("Description is required.");
  if (form.description?.trim().length > PRODUCT_FIELD_LIMITS.description) {
    errors.push(`Description must be ${PRODUCT_FIELD_LIMITS.description} characters or fewer.`);
  }
  if (!Number.isFinite(price) || price <= 0) errors.push("Price must be greater than zero.");
  const slashError = validateSlashPriceRule(price, compareAtPrice);
  if (slashError) errors.push(slashError);
  if (!images?.length) errors.push("Add at least one product image.");
  if (images?.length > MAX_PRODUCT_IMAGES) {
    errors.push(`Maximum ${MAX_PRODUCT_IMAGES} images allowed.`);
  }

  if (profile.showSizes) {
    const sizeTotal = sumSizeStock(form, sizeFields);
    if (sizeTotal <= 0) {
      errors.push("Enter stock for at least one size.");
    }
  } else if (form.quantity !== "" && (Number.isNaN(Number(form.quantity)) || Number(form.quantity) < 0)) {
    errors.push("Quantity must be zero or more.");
  }

  if (profile.brandRequired && !form.brand?.trim()) {
    errors.push("Brand is required for this category.");
  }

  profile.attributeFields.forEach((field) => {
    if (field.required && !form.attributes?.[field.key]?.trim()) {
      errors.push(`${field.label} is required for ${profile.title.toLowerCase()}.`);
    }
  });

  return errors;
};

export const productToForm = (product) => {
  if (!product) return { ...defaultProductForm };

  return {
    name: product.name || "",
    category: product.category || "",
    description: product.description || "",
    brand: product.brand || "",
    color: product.color || "",
    quantity: product.quantity ?? "",
    attributes: normalizeProductAttributes(product.attributes),
    sizeSmall: product.sizeSmall || "",
    sizeMedium: product.sizeMedium || "",
    sizeLarge: product.sizeLarge || "",
    sizeSmallextraLarge: product.sizeSmallextraLarge || "",
    sizeXXLarge: product.sizeXXLarge || "",
    sizeXXXLarge: product.sizeXXXLarge || "",
  };
};

export const validateProductEditForm = ({
  form,
  price,
  compareAtPrice,
  existingImages,
  removedImages,
  newImages,
}) => {
  const errors = [];
  const profile = getCategoryFormProfile(form.category);
  const sizeFields = getProfileSizeFields(form.category);
  const remaining = (existingImages || []).filter((url) => !(removedImages || []).includes(url));
  const totalImages = remaining.length + (newImages?.length || 0);

  if (!form.name?.trim()) errors.push("Product name is required.");
  if (form.name?.trim().length > PRODUCT_FIELD_LIMITS.name) {
    errors.push(`Product name must be ${PRODUCT_FIELD_LIMITS.name} characters or fewer.`);
  }
  if (!form.category?.trim() || form.category === "Select") errors.push("Category is required.");
  if (!form.description?.trim()) errors.push("Description is required.");
  if (form.description?.trim().length > PRODUCT_FIELD_LIMITS.description) {
    errors.push(`Description must be ${PRODUCT_FIELD_LIMITS.description} characters or fewer.`);
  }
  if (!Number.isFinite(price) || price <= 0) errors.push("Price must be greater than zero.");
  const slashError = validateSlashPriceRule(price, compareAtPrice);
  if (slashError) errors.push(slashError);
  if (totalImages === 0) errors.push("Product must have at least one image.");
  if (totalImages > MAX_PRODUCT_IMAGES) {
    errors.push(`Maximum ${MAX_PRODUCT_IMAGES} images allowed.`);
  }

  if (profile.showSizes) {
    const sizeTotal = sumSizeStock(form, sizeFields);
    if (sizeTotal <= 0) {
      errors.push("Enter stock for at least one size.");
    }
  } else if (form.quantity !== "" && (Number.isNaN(Number(form.quantity)) || Number(form.quantity) < 0)) {
    errors.push("Quantity must be zero or more.");
  }

  if (profile.brandRequired && !form.brand?.trim()) {
    errors.push("Brand is required for this category.");
  }

  profile.attributeFields.forEach((field) => {
    if (field.required && !form.attributes?.[field.key]?.trim()) {
      errors.push(`${field.label} is required for ${profile.title.toLowerCase()}.`);
    }
  });

  return errors;
};

export const buildProductUpdatePayload = (form, price, compareAtPrice) => {
  const profile = getCategoryFormProfile(form.category);
  const sizeFields = getProfileSizeFields(form.category);
  const sizeTotal = profile.showSizes ? sumSizeStock(form, sizeFields) : 0;

  const payload = {
    name: form.name.trim(),
    category: form.category.trim(),
    description: form.description.trim(),
    price,
    compareAtPrice: compareAtPrice > 0 ? compareAtPrice : "",
    quantity: profile.showSizes ? sizeTotal : form.quantity === "" ? 0 : Number(form.quantity),
    brand: form.brand?.trim() || "",
    color: form.color?.trim() || "",
  };

  sizeFields.forEach(([field]) => {
    payload[field] = profile.showSizes ? String(form[field] ?? "").trim() : "";
  });

  const attributes = form.attributes || {};
  payload.attributes = Object.fromEntries(
    Object.entries(attributes).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
  );

  return payload;
};

export const appendProductFormData = (formData, form, price, compareAtPrice) => {
  const profile = getCategoryFormProfile(form.category);
  const sizeFields = getProfileSizeFields(form.category);
  const sizeTotal = profile.showSizes ? sumSizeStock(form, sizeFields) : 0;

  formData.append("name", form.name.trim());
  formData.append("category", form.category.trim());
  formData.append("description", form.description.trim());
  formData.append("price", price);
  if (compareAtPrice > 0) {
    formData.append("compareAtPrice", compareAtPrice);
  }
  formData.append(
    "quantity",
    profile.showSizes ? sizeTotal : form.quantity === "" ? 0 : Number(form.quantity)
  );
  formData.append("brand", form.brand?.trim() || "");
  formData.append("color", form.color?.trim() || "");

  sizeFields.forEach(([field]) => {
    if (profile.showSizes) {
      formData.append(field, String(form[field] ?? "").trim());
    }
  });

  const attributes = form.attributes || {};
  const cleaned = Object.fromEntries(
    Object.entries(attributes).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "")
  );
  formData.append("attributes", JSON.stringify(cleaned));
};
